import { Box, Stack } from '@mui/material'
import { ReactComponent as EditLogo } from 'assets/table_edit_logo.svg'
import {
	formatExaminationFrequency,
	formatTreatmentDuration,
	getExaminationInterval,
	getTreatmentSchemaDiagnoses,
	getTreatmentSchemaName,
	resolveDiagnoseTitles,
} from 'components/cattles/treatmentPage/utils/treatmentSchemaDisplay'
import { ITreatmentSchema } from 'models/ICattlesData'
import { ReactNode, useEffect, useMemo } from 'react'
import { fetchDiagnosesList } from 'store/cattlesSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import Drawer from 'ui/Drawer'
import TooltipHover from 'ui/TooltipHover'
import { TypographyHeader14 } from 'ui/typography/TypographyHeader14'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'

interface Props {
	open: boolean
	onClose: () => void
	onEdit: () => void
	schema: ITreatmentSchema | null
}

const Section = ({
	title,
	children,
}: {
	title: string
	children: ReactNode
}) => (
	<Stack gap='8px'>
		<TypographyHeader14>{title}</TypographyHeader14>
		{children}
	</Stack>
)

const EMPTY_DIAGNOSES: { id: number; title: string }[] = []

const TreatmentViewDrawer = ({ open, onClose, onEdit, schema }: Props) => {
	const dispatch = useAppDispatch()
	const diagnosesList =
		useAppSelector(state => state.cattlesSlice.diagnosesList) ?? EMPTY_DIAGNOSES

	useEffect(() => {
		if (open && diagnosesList.length === 0) {
			dispatch(fetchDiagnosesList())
		}
	}, [open, diagnosesList.length, dispatch])

	const diagnoses = useMemo(
		() =>
			schema
				? resolveDiagnoseTitles(
						getTreatmentSchemaDiagnoses(schema),
						diagnosesList,
					).filter(diagnose => diagnose.title)
				: [],
		[schema, diagnosesList],
	)
	const schemaName = schema ? getTreatmentSchemaName(schema) : ''

	return (
		<Drawer
			open={open}
			onClose={onClose}
			title='Просмотр схемы лечения'
			anchor='right'
			width={520}
			headerActions={
				<TooltipHover title='Редактировать схему' placement='bottom' oneLine>
					<EditLogo
						width={24}
						height={24}
						style={{ cursor: 'pointer', display: 'block' }}
						onClick={onEdit}
					/>
				</TooltipHover>
			}
		>
			<Stack gap='24px' margin='0 24px 24px'>
				<Section title='Диагноз'>
					{diagnoses.length ? (
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
							{diagnoses.map(diagnose => (
								<Box
									key={`${diagnose.id}-${diagnose.title}`}
									sx={{
										padding: '8px 16px',
										backgroundColor: COLORS.background,
										borderRadius: '12px',
									}}
								>
									<TypographyPrimary>{diagnose.title}</TypographyPrimary>
								</Box>
							))}
						</Box>
					) : (
						<TypographyPrimary>Без диагноза</TypographyPrimary>
					)}
				</Section>

				<Section title='Схема лечения'>
					<Stack gap='16px'>
						<Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
							<TypographySecondary>Название схемы лечения:</TypographySecondary>
							<TypographyPrimary>{schemaName || '-'}</TypographyPrimary>
						</Box>
						<Stack gap='4px'>
							<TypographySecondary>Описание схемы лечения:</TypographySecondary>
							<TypographyPrimary sx={{ whiteSpace: 'pre-wrap' }}>
								{schema?.description?.trim() || '-'}
							</TypographyPrimary>
						</Stack>
					</Stack>
				</Section>

				<Section title='Продолжительность лечения'>
					<TypographyPrimary>
						{formatTreatmentDuration(schema?.duration)}
					</TypographyPrimary>
				</Section>

				<Section title='Периодичность осмотров'>
					<TypographyPrimary>
						{formatExaminationFrequency(
							schema ? getExaminationInterval(schema) : undefined,
						)}
					</TypographyPrimary>
				</Section>

				<Section title='Забраковать молоко на период'>
					<TypographyPrimary>
						{schema?.milk_reject_days
							? formatTreatmentDuration(schema.milk_reject_days)
							: 'Нет'}
					</TypographyPrimary>
				</Section>
			</Stack>
		</Drawer>
	)
}

export default TreatmentViewDrawer
